import type { ExportOptions } from '../types';

// GitHub-style CSS for HTML exports
const GITHUB_CSS = `
/* GitHub-style CSS for exported HTML */
body {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Noto Sans', Helvetica, Arial, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji';
  font-size: 16px;
  line-height: 1.6;
  color: #24292f;
  background-color: #ffffff;
  max-width: 980px;
  margin: 0 auto;
  padding: 32px;
}

.markdown-body {
  box-sizing: border-box;
  min-width: 200px;
  max-width: 980px;
  margin: 0 auto;
}

h1, h2, h3, h4, h5, h6 {
  margin-top: 24px;
  margin-bottom: 16px;
  font-weight: 600;
  line-height: 1.25;
}

h1 {
  font-size: 2em;
  border-bottom: 1px solid #d1d9e0;
  padding-bottom: 0.3em;
}

h2 {
  font-size: 1.5em;
  border-bottom: 1px solid #d1d9e0;
  padding-bottom: 0.3em;
}

h3 {
  font-size: 1.25em;
}

p {
  margin-top: 0;
  margin-bottom: 16px;
}

blockquote {
  padding: 0 1em;
  color: #656d76;
  border-left: 0.25em solid #d1d9e0;
  margin: 0 0 16px 0;
}

code {
  padding: 0.2em 0.4em;
  margin: 0;
  font-size: 85%;
  background-color: rgba(175, 184, 193, 0.2);
  border-radius: 6px;
  font-family: ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace;
}

pre {
  padding: 16px;
  overflow: auto;
  font-size: 85%;
  line-height: 1.45;
  background-color: #f6f8fa;
  border-radius: 6px;
  margin-bottom: 16px;
}

pre code {
  background-color: transparent;
  border: 0;
  padding: 0;
  margin: 0;
  font-size: 100%;
}

table {
  border-spacing: 0;
  border-collapse: collapse;
  display: block;
  width: max-content;
  max-width: 100%;
  overflow: auto;
  margin-bottom: 16px;
}

table th,
table td {
  padding: 6px 13px;
  border: 1px solid #d1d9e0;
}

table th {
  font-weight: 600;
  background-color: #f6f8fa;
}

table tr {
  background-color: #ffffff;
  border-top: 1px solid #d1d9e0;
}

table tr:nth-child(2n) {
  background-color: #f6f8fa;
}

ul, ol {
  margin-top: 0;
  margin-bottom: 16px;
  padding-left: 2em;
}

li {
  margin: 0.25em 0;
}

a {
  color: #0969da;
  text-decoration: none;
}

a:hover {
  text-decoration: underline;
}

img {
  max-width: 100%;
  height: auto;
  margin: 16px 0;
}
`;

export class ExportService {
  /**
   * Export content as HTML with embedded CSS
   */
  async exportHTML(content: string, options: ExportOptions): Promise<void> {
    try {
      // Generate HTML from markdown using react-markdown approach
      // For now, we'll use a simple markdown-to-HTML conversion
      // In a real implementation, we'd use the same react-markdown renderer from PreviewPanel
      const htmlContent = this.convertMarkdownToHTML(content);
      
      // Create complete HTML document with embedded CSS
      const filename = this.generateFilename(options.filename || 'document', 'html');
      const fullHTML = this.createHTMLDocument(htmlContent, options);
      
      // Trigger download
      this.downloadFile(fullHTML, filename, 'text/html');
      
    } catch (error) {
      console.error('HTML export failed:', error);
      throw new Error('Failed to export HTML. Please try again.');
    }
  }

  /**
   * Export current content as markdown file
   */
  async exportMarkdown(content: string, options: ExportOptions): Promise<void> {
    try {
      const filename = this.generateFilename(options.filename || 'document', 'md');
      
      // Use original markdown content for download
      this.downloadFile(content, filename, 'text/markdown');
      
    } catch (error) {
      console.error('Markdown export failed:', error);
      throw new Error('Failed to export Markdown. Please try again.');
    }
  }

  /**
   * Generate filename with timestamp if no custom name provided
   */
  private generateFilename(baseName: string, extension: string): string {
    // Extract title from content if baseName is generic
    if (baseName === 'document') {
      const timestamp = new Date().toISOString().slice(0, 19).replace(/[:\-]/g, '');
      return `content-studio-export-${timestamp}.${extension}`;
    }
    
    // Clean filename and ensure proper extension
    const cleanName = baseName.replace(/[^\w\-_\s]/g, '').replace(/\s+/g, '-');
    return `${cleanName}.${extension}`;
  }

  /**
   * Create complete HTML document with embedded CSS
   */
  private createHTMLDocument(htmlContent: string, options: ExportOptions): string {
    const css = options.customCSS || GITHUB_CSS;
    
    return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>Exported Document</title>
  <style>
${css}
  </style>
</head>
<body>
  <div class="markdown-body">
${htmlContent}
  </div>
</body>
</html>`;
  }

  /**
   * Trigger file download using Blob API
   */
  private downloadFile(content: string, filename: string, mimeType: string): void {
    try {
      // Create blob with proper encoding
      const blob = new Blob([content], { 
        type: `${mimeType};charset=utf-8` 
      });
      
      // Create download URL
      const url = URL.createObjectURL(blob);
      
      // Create temporary download link
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.style.display = 'none';
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('File download failed:', error);
      throw new Error('Failed to download file. Please try again.');
    }
  }

  /**
   * Simple markdown to HTML conversion
   * This is a basic implementation - in production we'd use the same react-markdown renderer
   */
  private convertMarkdownToHTML(content: string): string {
    // Enhanced markdown to HTML conversion
    // This provides better support for common markdown elements
    
    let html = content;
    
    // Remove HTML comments first
    html = html.replace(/<!--[\s\S]*?-->/g, '');
    
    // Handle code blocks (fenced)
    html = html.replace(/```(\w*)\n?([\s\S]*?)```/g, (_, lang, code) => {
      return `<pre><code class="language-${lang}">${this.escapeHtml(code.trim())}</code></pre>`;
    });
    
    // Handle inline code
    html = html.replace(/`([^`]+)`/g, '<code>$1</code>');
    
    // Handle images
    html = html.replace(/!\[([^\]]*)\]\(([^)]+)\)/g, '<img alt="$1" src="$2" />');
    
    // Handle links
    html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2">$1</a>');
    
    // Handle bold text
    html = html.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
    html = html.replace(/__([^_]+)__/g, '<strong>$1</strong>');
    
    // Handle italic text
    html = html.replace(/\*([^*]+)\*/g, '<em>$1</em>');
    html = html.replace(/_([^_]+)_/g, '<em>$1</em>');
    
    // Convert line by line for structure
    return html
      .split('\n')
      .map(line => {
        const trimmed = line.trim();
        
        // Headers
        if (trimmed.startsWith('# ')) {
          return `<h1>${trimmed.slice(2)}</h1>`;
        }
        if (trimmed.startsWith('## ')) {
          return `<h2>${trimmed.slice(3)}</h2>`;
        }
        if (trimmed.startsWith('### ')) {
          return `<h3>${trimmed.slice(4)}</h3>`;
        }
        if (trimmed.startsWith('#### ')) {
          return `<h4>${trimmed.slice(5)}</h4>`;
        }
        if (trimmed.startsWith('##### ')) {
          return `<h5>${trimmed.slice(6)}</h5>`;
        }
        if (trimmed.startsWith('###### ')) {
          return `<h6>${trimmed.slice(7)}</h6>`;
        }
        
        // Lists
        if (trimmed.startsWith('- ') || trimmed.startsWith('* ') || trimmed.startsWith('+ ')) {
          return `<li>${trimmed.slice(2)}</li>`;
        }
        if (/^\d+\.\s/.test(trimmed)) {
          return `<li>${trimmed.replace(/^\d+\.\s/, '')}</li>`;
        }
        
        // Blockquotes
        if (trimmed.startsWith('> ')) {
          return `<blockquote><p>${trimmed.slice(2)}</p></blockquote>`;
        }
        
        // Horizontal rules
        if (trimmed === '---' || trimmed === '***' || trimmed === '___') {
          return '<hr>';
        }
        
        // Empty lines
        if (trimmed === '') {
          return '<br>';
        }
        
        // Regular paragraphs (but not if already processed as HTML)
        if (!trimmed.startsWith('<') || !trimmed.endsWith('>')) {
          return `<p>${line}</p>`;
        }
        
        return line;
      })
      .join('\n');
  }
  
  /**
   * Escape HTML special characters
   */
  private escapeHtml(text: string): string {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * Extract title from markdown content
   */
  // @ts-ignore - Method kept for future use
  private _extractTitle(content: string): string {
    // Look for first h1 heading
    const h1Match = content.match(/^#\s+(.+)/m);
    if (h1Match) {
      return h1Match[1].trim();
    }
    
    // Look for any heading
    const headingMatch = content.match(/^#{1,6}\s+(.+)/m);
    if (headingMatch) {
      return headingMatch[1].trim();
    }
    
    // Fallback to first line or generic name
    const firstLine = content.split('\n')[0].trim();
    return firstLine || 'Untitled Document';
  }
}

// Export singleton instance
export const exportService = new ExportService();